import { DestroyRef, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";

/**
 * A load function used by `BackgroundRefreshCoordinator`. Called with `redirectOnMissingData=true`
 * exactly once for the initial page load, and `false` for every subsequent background-triggered
 * reload (a background reload should never yank the user off a page they're already using).
 * Returns whether the load actually succeeded (i.e. whether the initial load should proceed).
 */
export type BackgroundRefreshLoadFn = (redirectOnMissingData: boolean) => Promise<boolean>;

/**
 * Coordinates a page's initial data load with a "something changed elsewhere, re-read your local
 * data" notification stream (e.g. `ConstructionManagerScheduleService.scheduleRefreshed`).
 *
 * Two problems this solves that a plain `subject.subscribe(() => reload())` doesn't:
 * 1. The notification is a plain (non-replaying) `Subject`, so a naive "subscribe after the initial
 *    load resolves" pattern silently drops any emission that lands *during* that initial load. This
 *    coordinator subscribes immediately (before the initial load starts) and queues a single trailing
 *    reload if that happens.
 * 2. Rapid/overlapping emissions are coalesced into one trailing reload instead of running the loader
 *    concurrently (which could interleave two loads and apply a stale result over a fresh one) or
 *    piling up a queue of redundant reloads.
 *
 * Deliberately a plain class, constructed per-use (`new BackgroundRefreshCoordinator(...)`) rather than
 * an injectable service: its busy/pending-refresh state and `load` closure are inherently specific to
 * one page's ngOnInit call, so a singleton (or even a component-provided instance resolved via DI)
 * would either leak state across unrelated pages or need a factory anyway to supply the per-call
 * `load` function - `new` is simpler and just as testable.
 */
export class BackgroundRefreshCoordinator {
  private isBusy = false;
  private hasPendingRefresh = false;

  // Fires once whenever a background-triggered (non-initial) reload successfully re-reads local
  // data - i.e. exactly when it's safe/correct to flash the "data updated" fade on-screen.
  readonly dataUpdatedPulse = signal(false);

  constructor(
    refreshed$: Observable<unknown>,
    private readonly loadData: BackgroundRefreshLoadFn,
    destroyRef: DestroyRef
  ) {
    refreshed$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
      this.hasPendingRefresh = true;
      void this.drainPendingRefresh();
    });
  }

  async runInitialLoad(): Promise<boolean> {
    this.isBusy = true;
    const redirectOnMissingData = true;
    try {
      return await this.loadData(redirectOnMissingData);
    } finally {
      this.isBusy = false;
      void this.drainPendingRefresh();
    }
  }

  private async drainPendingRefresh(): Promise<void> {
    if (this.isBusy || !this.hasPendingRefresh) {
      return;
    }

    this.hasPendingRefresh = false;
    this.isBusy = true;
    try {
      const redirectOnMissingData = false;
      const succeeded = await this.loadData(redirectOnMissingData);
      if (succeeded) {
        this.dataUpdatedPulse.set(true);
      }
    } finally {
      this.isBusy = false;
      // Another refresh may have landed while this one was running; drain it too (coalesced, not queued).
      void this.drainPendingRefresh();
    }
  }
}
